import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Box, { BoxProps } from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { MAIN_CONTENT_ID } from 'src/components/skip-link';
import { useSettingsContext } from 'src/context/theme-settings-context';

import { HEADER, NAV } from '../config-layout';

// ----------------------------------------------------------------------

const SPACING = 20;

export function Main({ children, sx, ...other }: BoxProps) {
  const settings = useSettingsContext();
  const theme = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isNavMini = settings.themeLayout === 'mini';

  const mainRef = useRef<HTMLElement>(null);
  const lastFocused = useRef<Element | null>(null);

  useEffect(() => {
    const track = (event: FocusEvent) => {
      lastFocused.current = event.target as Element;
    };
    document.addEventListener('focusin', track);
    return () => document.removeEventListener('focusin', track);
  }, []);

  // Opening a page from a row or card unmounts the element that had focus, dropping focus to
  // <body> — the next Tab would restart at the top of the header and sidebar. Hand focus to the
  // page instead. Only when focus was actually lost: a sidebar link keeps it, and on a fresh
  // load nothing had focus yet, so the first Tab still reaches the skip link.
  useEffect(() => {
    const focusWasLost = !!lastFocused.current && !lastFocused.current.isConnected;
    if (focusWasLost && document.activeElement === document.body) {
      mainRef.current?.focus({ preventScroll: true });
    }
  }, [location.pathname]);

  const isPageWithoutMarginBottom = useMemo(() => {
    const tab = searchParams.get('tab');
    return (
      (location.pathname.includes('/lecture/') &&
        (tab === 'content' || tab === 'practice' || tab === null)) ||
      (location.pathname.includes('/exam/') && (tab === 'questions' || !tab))
    );
  }, [location.pathname, searchParams]);

  return (
    <Box
      ref={mainRef}
      component="main"
      id={MAIN_CONTENT_ID}
      tabIndex={-1}
      sx={{
        flexGrow: 1,
        minHeight: isPageWithoutMarginBottom
          ? `calc(100vh - ${HEADER.H_MOBILE + SPACING}px)`
          : `calc(100vh - ${HEADER.H_MOBILE}px - ${HEADER.H_MOBILE + SPACING}px)`,
        backgroundColor: theme.palette.background.contrast,
        flexDirection: 'column',
        pt: `${HEADER.H_MOBILE + SPACING}px`,
        pb: isPageWithoutMarginBottom ? 0 : `${HEADER.H_MOBILE}px`,
        ...(lgUp && {
          px: 2,
          pt: `${HEADER.H_DESKTOP + SPACING}px`,
          pb: isPageWithoutMarginBottom ? 0 : `${HEADER.H_DESKTOP}px`,
          minHeight: isPageWithoutMarginBottom
            ? `calc(100vh - ${HEADER.H_DESKTOP + SPACING}px)`
            : `calc(100vh - ${HEADER.H_DESKTOP}px - ${HEADER.H_DESKTOP + SPACING}px)`,
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}
